import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

export default function LoginPage() {
  const { login, verifyTotp, isLoading } = useAuth();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [stage,    setStage]    = useState<'password' | 'totp'>('password');
  const [challenge, setChallenge] = useState<string>('');
  const [totpCode, setTotpCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const result = await login(email, password);
      if (result.totpRequired && result.challenge) {
        setChallenge(result.challenge);
        setStage('totp');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })
        ?.response?.data?.error ?? 'Anmeldung fehlgeschlagen.';
      setError(msg);
    }
  }

  async function handleTotpSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await verifyTotp(challenge, totpCode.trim(), useBackup);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })
        ?.response?.data?.error ?? '2FA-Code ungültig.';
      setError(msg);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F8F7F4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '"DM Sans", sans-serif',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: '48px 40px',
        width: 400,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        {/* Logo / Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img
            src="/assets/logo.svg"
            alt="Rubin Real Estate"
            style={{ height: 80, marginBottom: 16 }}
          />
          <p style={{ margin: 0, color: '#888', fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Investment CRM
          </p>
        </div>

        {stage === 'totp' ? (
          <form onSubmit={handleTotpSubmit}>
            <p style={{ margin: '0 0 16px', color: '#555', fontSize: 13 }}>
              {useBackup
                ? 'Gib einen Backup-Code ein (Format xxxx-xxxx).'
                : 'Gib den 6-stelligen Code aus deiner Authenticator-App ein.'}
            </p>
            <input
              type="text"
              inputMode={useBackup ? 'text' : 'numeric'}
              value={totpCode}
              onChange={e => setTotpCode(e.target.value)}
              required
              autoFocus
              placeholder={useBackup ? 'xxxx-xxxx' : '123456'}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', border: '1px solid #ddd',
                borderRadius: 8, fontSize: 18, letterSpacing: useBackup ? 0 : 4,
                textAlign: 'center', outline: 'none', marginBottom: 12,
              }}
            />
            {error && (
              <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16 }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              style={{ width: '100%', padding: '11px', background: isLoading ? '#e8a090' : '#C8402A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: isLoading ? 'not-allowed' : 'pointer' }}
            >
              {isLoading ? 'Prüfe…' : 'Bestätigen'}
            </button>
            <button
              type="button"
              onClick={() => { setUseBackup(!useBackup); setTotpCode(''); setError(''); }}
              style={{ marginTop: 12, background: 'none', border: 'none', color: '#888', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
            >
              {useBackup ? 'Authenticator-Code verwenden' : 'Backup-Code verwenden'}
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#333' }}>
              E-Mail
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="name@rubin.estate"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', border: '1px solid #ddd',
                borderRadius: 8, fontSize: 14, outline: 'none',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => (e.target.style.borderColor = '#C8402A')}
              onBlur={e  => (e.target.style.borderColor = '#ddd')}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#333' }}>
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', border: '1px solid #ddd',
                borderRadius: 8, fontSize: 14, outline: 'none',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => (e.target.style.borderColor = '#C8402A')}
              onBlur={e  => (e.target.style.borderColor = '#ddd')}
            />
          </div>

          {error && (
            <div style={{
              padding: '10px 12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#dc2626',
              fontSize: 14,
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '11px',
              background: isLoading ? '#e8a090' : '#C8402A',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {isLoading ? 'Anmelden...' : 'Anmelden'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
